/* eslint-disable max-len */

import Vue from 'vue';
import VueRouter from 'vue-router';
import AuthenticationService from '@/auth.service';
import { PermissionType } from '@shared/common/types';
import { User } from '@/modules/user/user';
import { SocketIOService } from '@/socketio.service';

Vue.use(VueRouter);

type importFunc = (path: string) => Promise<any>;

const routes = [
  {
    path: '/',
    name: 'login',
    component: (): Promise<any> => import(/* webpackChunkName: "login" */ '../modules/auth/pages/Login.vue'),
  },
  {
    path: '/home',
    name: 'home',
    component: (): Promise<any> => import(/* webpackChunkName: "home" */ '../modules/home/pages/Home.vue'),
  },
  {
    path: '/profile',
    name: 'profile',
    component: (): Promise<any> => import(/* webpackChunkName: "profile" */ '../modules/user/pages/Profile.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.viewDevice],
    },
    path: '/devices',
    name: 'devices',
    component: (): Promise<any> => import(/* webpackChunkName: "devices" */ '../modules/device/pages/DeviceList.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.createDevice],
    },
    path: '/devices/new',
    name: 'deviceCreation',
    component: (): Promise<any> => import(/* webpackChunkName: "devices" */ '../modules/device/pages/DeviceEdit.vue'),
  },
  {
    path: '/devices/:id',
    name: 'deviceView',
    component: (): Promise<any> => import(/* webpackChunkName: "devices" */ '../modules/device/pages/DeviceView.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.updateDevice],
    },
    path: '/devices/:id/edit',
    name: 'deviceEdit',
    component: (): Promise<any> => import(/* webpackChunkName: "devices" */ '../modules/device/pages/DeviceEdit.vue'),
  },
  {
    path: '/alerts',
    name: 'alerts',
    component: (): Promise<any> => import(/* webpackChunkName: "alerts" */ '../modules/alert/pages/AlertList.vue'),
  },
  {
    path: '/technical-messages',
    name: 'technicalMessages',
    component: (): Promise<any> => import(/* webpackChunkName: "technicalMessages" */ '../modules/technical-message/pages/TechnicalMessageList.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.viewSubscription],
    },
    path: '/subscriptions',
    name: 'subscriptionList',
    component: (): Promise<any> => import(/* webpackChunkName: "subscriptions" */ '../modules/subscription/pages/SubscriptionList.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.createSubscription],
    },
    path: '/subscriptions/new',
    name: 'subscriptionCreation',
    component: (): Promise<any> => import(/* webpackChunkName: "subscriptions" */ '../modules/subscription/pages/SubscriptionEdit.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.updateSubscription],
    },
    path: '/subscriptions/:id/edit',
    name: 'subscriptionEdit',
    component: (): Promise<any> => import(/* webpackChunkName: "subscriptions" */ '../modules/subscription/pages/SubscriptionEdit.vue'),
  },
  {
    path: '/users',
    name: 'userList',
    meta: {
      // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
      canActivate(to: any): boolean {
        const user = AuthenticationService.user as User;

        if (to.query.role) {
          return user.can(PermissionType.viewUser) && user.canViewRoles(to.query.role);
        }

        return user.can(PermissionType.viewUser);
      },
    },
    component: (): Promise<any> => import(/* webpackChunkName: "users" */ '../modules/user/pages/UserList.vue'),
  },
  {
    // meta: {
    //   permissions: [PermissionType.createUser],
    // },
    path: '/users/new',
    name: 'userCreation',
    component: (): Promise<any> => import(/* webpackChunkName: "users" */ '../modules/user/pages/UserEdit.vue'),
  },
  {
    path: '/users/:id',
    name: 'userView',
    component: (): Promise<any> => import(/* webpackChunkName: "users" */ '../modules/user/pages/UserView.vue'),
  },
  {
    path: '/users/:id/edit',
    name: 'userEdit',
    component: (): Promise<any> => import(/* webpackChunkName: "users" */ '../modules/user/pages/UserEdit.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.viewAgency],
    },
    path: '/agencies',
    name: 'agencyList',
    component: (): Promise<any> => import(/* webpackChunkName: "agencies" */ '../modules/agency/pages/AgencyList.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.createAgency],
    },
    path: '/agencies/new',
    name: 'agencyCreation',
    component: (): Promise<any> => import(/* webpackChunkName: "agencies" */ '../modules/agency/pages/AgencyEdit.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.updateAgency],
    },
    path: '/agencies/:id/edit',
    name: 'agencyEdit',
    component: (): Promise<any> => import(/* webpackChunkName: "agencies" */ '../modules/agency/pages/AgencyEdit.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.viewTagsTable],
    },
    path: '/tags',
    name: 'tagList',
    component: (): Promise<any> => import(/* webpackChunkName: "tags" */ '../modules/tag/pages/TagList.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.createTag],
    },
    path: '/tags/new',
    name: 'tagCreation',
    component: (): Promise<any> => import(/* webpackChunkName: "tags" */ '../modules/tag/pages/TagEdit.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.updateTag],
    },
    path: '/tags/:id/edit',
    name: 'tagEdit',
    component: (): Promise<any> => import(/* webpackChunkName: "tags" */ '../modules/tag/pages/TagEdit.vue'),
  },
  {
    path: '/breadcrumbs',
    name: 'breadcrumbList',
    component: (): Promise<any> => import(/* webpackChunkName: "breadcrumbs" */ '../modules/breadcrumb/pages/BreadcrumbList.vue'),
  },
  {
    path: '/activate_account',
    name: 'accountActivation',
    meta: {
      noAuth: true,
    },
    component: (): Promise<any> => import(/* webpackChunkName: "accountActivation" */ '../modules/user/pages/AccountActivation.vue'),
  },
  {
    meta: {
      permissions: [PermissionType.viewPvLog],
    },
    path: '/pv-logs',
    name: 'pvLogList',
    component: (): Promise<any> => import(/* webpackChunkName: "tags" */ '../modules/pv-log/pages/PvLogList.vue'),
  },
  { path: '*', redirect: '/' },
  // {
  //   path: '/about',
  //   name: 'about',
  //   // route level code-splitting
  //   // this generates a separate chunk (about.[hash].js) for this route
  //   // which is lazy-loaded when the route is visited.
  //   component: () => import(/* webpackChunkName: "about" */ '../views/About.vue'),
  // },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// Style changes depending on route
router.beforeEach((to, from, next) => {
  const html = document.querySelector('html');

  if (html) {
    if (to.meta && to.meta.noAuth) {
      html.classList.remove('has-aside-left');
    } else {
      html.classList.add('has-aside-left');
    }
  }

  next();
});

// Authentication guard
router.beforeEach(async (to, from, next) => {
  if (to.meta && to.meta.noAuth) {
    return next();
  }

  if (AuthenticationService.user) {
    handleAuthentifiedUser(to, from, next);
  } else {
    try {
      await AuthenticationService.getUser();
      const user = AuthenticationService.user as unknown as User;

      if (user.can(PermissionType.useWebApp)) {
        handleAuthentifiedUser(to, from, next);
      } else {
        throw new Error();
      }
    } catch (e) {
      if (to.path !== '/') {
        next('/');
      } else {
        next();
      }
    }
  }
});

// Authorization guard
router.beforeEach((to, from, next) => {
  if (isRouteAuthorized(to)) {
    next();
  } else {
    next('/');
  }
});

const handleAuthentifiedUser = (to: any, from: any, next: any): void => {
  if (!SocketIOService.initialized) {
    SocketIOService.init();
  }

  if (to.path === '/') {
    next('/home');
  } else {
    next();
  }
}

export const isRouteAuthorized = (route: string | object): boolean => {
  let routeConfig: any;

  if (typeof route === 'string') {
    routeConfig = routes.find((routeConf) => routeConf.name === route);
  } else {
    routeConfig = route;
  }


  if (!routeConfig) {
    return false;
  }

  if (routeConfig.meta && routeConfig.meta.canActivate) {
    return routeConfig.meta.canActivate(routeConfig);
  }

  if (routeConfig.meta && routeConfig.meta.roles) {
    return !!AuthenticationService.user && AuthenticationService.user.is(routeConfig.meta.roles);
  }

  if (routeConfig.meta && routeConfig.meta.permissions) {
    return !!AuthenticationService.user && AuthenticationService.user.can(routeConfig.meta.permissions);
  }

  return true;
}

export default router;
